import * as React from 'react';
import useCsrfFetchJson from './hooks/use_csrf_fetch';
import './mud.css';

const BASIC_COLORS = [
  '#5b5b5b', '#800000', '#008000', '#808000',
  '#000080', '#800080', '#008080', '#c0c0c0'
];
const BRIGHT_COLORS = [
  '#b0b0b0', '#ff0000', '#00ff00', '#ffff00',
  '#0000ff', '#ff00ff', '#00ffff', '#ffffff'
];

const xtermColor = (index) => {
  if (index < 16) {
    return index < 8 ? BASIC_COLORS[index] : BRIGHT_COLORS[index - 8];
  }
  if (index >= 16 && index <= 231) {
    const idx = index - 16;
    const r = Math.floor(idx / 36);
    const g = Math.floor((idx % 36) / 6);
    const b = idx % 6;
    const toHex = (v) => {
      const val = v === 0 ? 0 : 55 + (v * 40);
      return val.toString(16).padStart(2, '0');
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }
  if (index >= 232 && index <= 255) {
    const gray = 8 + (index - 232) * 10;
    const hex = gray.toString(16).padStart(2, '0');
    return `#${hex}${hex}${hex}`;
  }
  return null;
};

const escapeHtml = (str) => str
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

const parseSendAttributes = (attrText) => {
  const hrefMatch = attrText.match(/href="([^"]+)"/i);
  if (hrefMatch) return hrefMatch[1];
  const quotedMatch = attrText.match(/"([^"]+)"/);
  if (quotedMatch) return quotedMatch[1];
  return null;
};

const parseSendHints = (attrText) => {
  const hintMatch = attrText.match(/hint="([^"]+)"/i);
  if (!hintMatch) return null;
  return hintMatch[1].split('|').map((s) => s.trim()).filter(Boolean);
};

const renderMxp = (text) => {
  let out = '';
  let cursor = 0;
  const openRe = /<send\s+([^>]*)>/ig;
  let match;
  while ((match = openRe.exec(text)) !== null) {
    const start = match.index;
    const attrText = match[1] || '';
    const href = parseSendAttributes(attrText);
    const hints = parseSendHints(attrText);
    const before = text.slice(cursor, start);
    if (before) out += escapeHtml(before);
    const afterOpen = match.index + match[0].length;
    const closeIdx = text.indexOf('</send>', afterOpen);
    if (closeIdx === -1) {
      out += escapeHtml(text.slice(afterOpen));
      cursor = text.length;
      break;
    }
    const inner = text.slice(afterOpen, closeIdx);
    const safeInner = escapeHtml(inner);
    const commands = href ? href.split('|').map((s) => s.trim()).filter(Boolean) : [];
    if (commands.length <= 1) {
      const cmd = commands[0] || '';
      out += `<a data-mxp-send="${escapeHtml(cmd)}" style="color:#9cf;text-decoration:underline;cursor:pointer;">${safeInner}</a>`;
    } else {
      const options = commands.map((cmd, idx) => {
        const label = (hints && hints[idx]) ? hints[idx] : cmd;
        return `<button data-mxp-send="${escapeHtml(cmd)}" style="display:block;width:100%;text-align:left;padding:0.25rem 0.5rem;background:#111;color:#9cf;border:none;cursor:pointer;">${escapeHtml(label)}</button>`;
      }).join('');
      out += `
        <details style="display:inline-block;">
          <summary style="color:#9cf;cursor:pointer;text-decoration:underline;list-style:none;">${safeInner}</summary>
          <div style="background:#000;border:1px solid #333;padding:0.25rem 0;position:relative;z-index:2;">
            ${options}
          </div>
        </details>
      `;
    }
    cursor = closeIdx + 7;
    openRe.lastIndex = cursor;
  }
  if (cursor < text.length) {
    out += escapeHtml(text.slice(cursor));
  }
  return out;
};

const applySgr = (codes, state) => {
  for (let i = 0; i < codes.length; i += 1) {
    const code = codes[i] === '' ? 0 : Number(codes[i]);
    if (Number.isNaN(code)) continue;
    if (code === 0) {
      state.fg = null;
      state.bg = null;
      state.bold = false;
    } else if (code === 1) {
      state.bold = true;
    } else if (code === 22) {
      state.bold = false;
    } else if (code === 39) {
      state.fg = null;
    } else if (code === 49) {
      state.bg = null;
    } else if (code === 38 || code === 48) {
      const mode = codes[i + 1];
      const value = codes[i + 2];
      if (mode === '5' && value !== undefined) {
        const color = xtermColor(Number(value));
        if (code === 38) state.fg = color;
        if (code === 48) state.bg = color;
        i += 2;
      }
    } else if (code >= 30 && code <= 37) {
      state.fg = BASIC_COLORS[code - 30];
    } else if (code >= 90 && code <= 97) {
      state.fg = BRIGHT_COLORS[code - 90];
    } else if (code >= 40 && code <= 47) {
      state.bg = BASIC_COLORS[code - 40];
    } else if (code >= 100 && code <= 107) {
      state.bg = BRIGHT_COLORS[code - 100];
    }
  }
};

const renderAnsiToHtml = (text, state) => {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '');
  const regex = /\x1b\[[0-9;]*m/g;
  let lastIndex = 0;
  let result = '';
  let match;
  while ((match = regex.exec(normalized)) !== null) {
    const chunk = normalized.slice(lastIndex, match.index);
    if (chunk) {
      const style = [
        state.fg ? `color: ${state.fg};` : '',
        state.bg ? `background-color: ${state.bg};` : '',
        state.bold ? 'font-weight: bold;' : ''
      ].join(' ');
      const safe = renderMxp(chunk);
      result += style.trim() ? `<span style="${style}">${safe}</span>` : safe;
    }
    const codes = match[0].slice(2, -1).split(';');
    applySgr(codes, state);
    lastIndex = regex.lastIndex;
  }
  const tail = normalized.slice(lastIndex);
  if (tail) {
    const style = [
      state.fg ? `color: ${state.fg};` : '',
      state.bg ? `background-color: ${state.bg};` : '',
      state.bold ? 'font-weight: bold;' : ''
    ].join(' ');
    const safe = renderMxp(tail);
    result += style.trim() ? `<span style="${style}">${safe}</span>` : safe;
  }
  return result;
};

const decodeBase64Utf8 = (b64) => {
  const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  return new TextDecoder('utf-8').decode(bytes);
};

const MudClient = () => {
  const [status, setStatus] = React.useState('disconnected');
  const [input, setInput] = React.useState('');
  const [inputType, setInputType] = React.useState('text');
  const [outputHtml, setOutputHtml] = React.useState('');
  const [hasStarted, setHasStarted] = React.useState(false);
  const [humanChecked, setHumanChecked] = React.useState(false);
  const [startDelaySeconds, setStartDelaySeconds] = React.useState(() => 2 + Math.floor(Math.random() * 4));
  const [delayRemaining, setDelayRemaining] = React.useState(null);
  const [history, setHistory] = React.useState([]);
  const [historyIndex, setHistoryIndex] = React.useState(-1);
  const [historyDraft, setHistoryDraft] = React.useState('');
  const [commandCounts, setCommandCounts] = React.useState({});
  const [characters, setCharacters] = React.useState([]);
  const [selectedCharacterId, setSelectedCharacterId] = React.useState('');
  const [otpError, setOtpError] = React.useState(null);
  const [otpLoading, setOtpLoading] = React.useState(false);
  const [pendingOtpToken, setPendingOtpToken] = React.useState(null);
  const outputRef = React.useRef(null);
  const subRef = React.useRef(null);
  const ansiStateRef = React.useRef({ fg: null, bg: null, bold: false });
  const inputRef = React.useRef(null);
  const fetchJson = useCsrfFetchJson();

  React.useEffect(() => {
    if (!hasStarted) return undefined;
    if (!window.ActionCableConsumer) {
      setStatus('missing ActionCable');
      return undefined;
    }
    setStatus('connecting');
    const subscription = window.ActionCableConsumer.subscriptions.create(
      { channel: 'MudProxyChannel' },
      {
        connected: () => setStatus('connected'),
        disconnected: () => setStatus('disconnected'),
        received: (data) => {
          if (data.type === 'data') {
            const text = decodeBase64Utf8(data.data || '');
            const html = renderAnsiToHtml(text, ansiStateRef.current);
            setOutputHtml((prev) => prev + html);
            if (/Password:\s*$/i.test(text) || /Password:/i.test(text)) {
              setInputType('password');
            } else if (/Name:\s*$/i.test(text) || /Please enter your name:/i.test(text)) {
              setInputType('text');
            }
          } else if (data.type === 'closed') {
            setStatus('closed');
          } else if (data.type === 'error') {
            setStatus(`error: ${data.message}`);
          } else if (data.type === 'status') {
            setStatus(data.message);
          }
        }
      }
    );
    subRef.current = subscription;
    return () => {
      subscription.unsubscribe();
    };
  }, [hasStarted]);

  React.useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [outputHtml]);

  React.useEffect(() => {
    if (hasStarted && inputRef.current) {
      inputRef.current.focus();
    }
  }, [hasStarted]);

  React.useEffect(() => {
    if (!humanChecked || hasStarted) return;
    setDelayRemaining(startDelaySeconds);
    const timer = setInterval(() => {
      setDelayRemaining((prev) => {
        if (prev === null) return prev;
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [humanChecked, hasStarted, startDelaySeconds]);

  React.useEffect(() => {
    if (!humanChecked && !hasStarted) {
      setDelayRemaining(null);
      setStartDelaySeconds(2 + Math.floor(Math.random() * 4));
    }
  }, [humanChecked, hasStarted]);

  React.useEffect(() => {
    if (!humanChecked) return;
    let active = true;
    const loadCharacters = async () => {
      try {
        const list = await fetchJson('/player/characters/mine');
        if (!active) return;
        const items = Array.isArray(list) ? list : [];
        setCharacters(items);
        if (!selectedCharacterId && items.length > 0) {
          setSelectedCharacterId(items[0].id);
        }
      } catch (err) {
        if (active) {
          setCharacters([]);
        }
      }
    };
    loadCharacters();
    return () => {
      active = false;
    };
  }, [fetchJson, humanChecked, selectedCharacterId]);

  React.useEffect(() => {
    if (status === 'connected' && pendingOtpToken) {
      if (subRef.current) {
        subRef.current.send({ data: `${pendingOtpToken}\r\n` });
      }
      setPendingOtpToken(null);
    }
  }, [status, pendingOtpToken]);

  const sendRaw = (cmd) => {
    if (!subRef.current || !hasStarted) return;
    subRef.current.send({ data: `${cmd}\r\n` });
  };

  const sendLine = () => {
    if (!subRef.current) return;
    const trimmed = input.trim();
    subRef.current.send({ data: `${input}\r\n` });
    if (trimmed && inputType !== 'password') {
      setHistory((prev) => [...prev.slice(-199), trimmed]);
      setCommandCounts((prev) => {
        const next = { ...prev };
        next[trimmed] = (next[trimmed] || 0) + 1;
        return next;
      });
    }
    setInput('');
    setHistoryIndex(-1);
    setHistoryDraft('');
    if (inputType === 'password') {
      setInputType('text');
    }
  };

  const handleOtpLogin = async () => {
    if (!selectedCharacterId) return;
    setOtpError(null);
    setOtpLoading(true);
    try {
      const response = await fetchJson(`/player/characters/${selectedCharacterId}/otp`);
      const token = response && response.otp;
      if (!token) {
        setOtpError('Unable to fetch OTP.');
        setOtpLoading(false);
        return;
      }
      if (!hasStarted) {
        setHasStarted(true);
      }
      if (status === 'connected' && subRef.current) {
        subRef.current.send({ data: `${token}\r\n` });
      } else {
        setPendingOtpToken(token);
      }
    } catch (err) {
      setOtpError(err.message || 'Unable to fetch OTP.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleOutputClick = (event) => {
    const target = event.target.closest('[data-mxp-send]');
    if (!target || !subRef.current) return;
    const command = target.getAttribute('data-mxp-send');
    if (command) {
      sendRaw(command);
    }
  };

  const onKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      sendLine();
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!history.length) return;
      if (historyIndex === -1) {
        setHistoryDraft(input);
        setHistoryIndex(history.length - 1);
        setInput(history[history.length - 1]);
      } else if (historyIndex > 0) {
        setHistoryIndex(historyIndex - 1);
        setInput(history[historyIndex - 1]);
      }
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (historyIndex === -1) return;
      if (historyIndex < history.length - 1) {
        setHistoryIndex(historyIndex + 1);
        setInput(history[historyIndex + 1]);
      } else {
        setHistoryIndex(-1);
        setInput(historyDraft);
      }
    }
  };

  const startEnabled = humanChecked && delayRemaining === 0;

  const commonCommands = Object.entries(commandCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([cmd]) => cmd)
    .filter((cmd) => cmd && !['look', 'who', 'score'].includes(cmd))
    .slice(0, 3);

  return (
    <div className="lots-shell">
      <div className="lots-container mud-client-shell">
        <div className="lots-hero">
          <div className="portal-kicker">Play Online (alpha)</div>
          <h1 className="lots-hero__title">Launch the cockpit</h1>
          <p className="lots-hero__subtitle">
            This browser client is experimental. It is great for quick sessions, but dedicated MUD clients are still
            the most reliable. Use OTP login for the fastest entry.
          </p>
        </div>

        <div className="mud-client-layout">
          <div className="mud-client-panel">
            <h3>Session Control</h3>
            <div className="mud-client-status">
              <span className="lots-pill">Status: {status}</span>
            </div>
            {!hasStarted && (
              <div style={{ marginTop: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={humanChecked}
                    onChange={(e) => setHumanChecked(e.target.checked)}
                    style={{ marginRight: '0.5rem' }}
                  />
                  I am human
                </label>
                {humanChecked && delayRemaining !== null && delayRemaining > 0 && (
                  <div className="portal-note" style={{ marginBottom: '0.5rem' }}>
                    Enabling in {delayRemaining}s...
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setHasStarted(true)}
                  className="lots-btn"
                  disabled={!startEnabled}
                >
                  Start Session
                </button>
              </div>
            )}

            <div style={{ marginTop: '1rem' }}>
              <div className="portal-kicker">Login</div>
              <p className="portal-note">Select a character and use OTP to jump straight in.</p>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <select
                  value={selectedCharacterId}
                  onChange={(e) => setSelectedCharacterId(e.target.value)}
                  style={{ padding: '0.35rem 0.5rem' }}
                  disabled={!humanChecked}
                >
                  {characters.length === 0 && <option value="">No characters</option>}
                  {characters.map((ch) => (
                    <option key={ch.id} value={ch.id}>{ch.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handleOtpLogin}
                  disabled={!selectedCharacterId || otpLoading || !humanChecked}
                  className="lots-btn lots-btn--teal"
                >
                  {otpLoading ? 'Fetching OTP...' : 'Login via OTP'}
                </button>
              </div>
              {otpError && <div style={{ color: '#c66', marginTop: '0.5rem' }}>{otpError}</div>}
            </div>

            <div style={{ marginTop: '1.5rem' }}>
              <div className="portal-kicker">New here?</div>
              <div className="lots-grid">
                <div className="portal-highlight">Type <strong>look</strong> to re-orient after login.</div>
                <div className="portal-highlight">Use <strong>who</strong> to see who is online.</div>
                <div className="portal-highlight">Try <strong>score</strong> to check stats.</div>
              </div>
            </div>
          </div>

          <div className="mud-client-output-wrap">
            <div style={{ position: 'relative' }}>
              <div
                ref={outputRef}
                onClick={handleOutputClick}
                className="mud-output"
                dangerouslySetInnerHTML={{ __html: outputHtml }}
              />
              <div className="mud-compass">
        {[
          { label: 'NW', cmd: 'nw' },
          { label: 'N', cmd: 'n' },
          { label: 'NE', cmd: 'ne' },
          { label: 'W', cmd: 'w' },
          { label: 'CENTER', cmd: null },
          { label: 'E', cmd: 'e' },
          { label: 'SW', cmd: 'sw' },
          { label: 'S', cmd: 's' },
          { label: 'SE', cmd: 'se' }
        ].map((item) => {
          if (item.label === 'CENTER') {
            return (
                <div key="center" className="mud-compass-center">
                  <button
                    type="button"
                    onClick={() => {
                      if (!subRef.current || !hasStarted) return;
                      subRef.current.send({ data: 'u\r\n' });
                    }}
                    disabled={!hasStarted}
                    title="u"
                  >
                    U
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!subRef.current || !hasStarted) return;
                      subRef.current.send({ data: 'd\r\n' });
                    }}
                    disabled={!hasStarted}
                    title="d"
                  >
                    D
                  </button>
                </div>
            );
          }
          return (
              <button
                key={item.cmd}
                type="button"
                onClick={() => {
                  sendRaw(item.cmd);
                }}
                disabled={!hasStarted}
                title={item.cmd}
              >
                {item.label}
              </button>
          );
        })}
              </div>
            </div>
            <div className="mud-input-row">
              <input
                type={inputType}
                value={input}
                ref={inputRef}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                className="mud-input"
                disabled={!hasStarted}
                placeholder="Type a command and press Enter"
              />
              <button
                type="button"
                onClick={sendLine}
                className="lots-btn"
                disabled={!hasStarted}
              >
                Send
              </button>
            </div>
            <div className="mud-toolbar" style={{ marginTop: '0.5rem' }}>
              {['look', 'who', 'score', ...commonCommands].map((cmd) => (
                <button
                  key={cmd}
                  type="button"
                  onClick={() => {
                    sendRaw(cmd);
                  }}
                  className="lots-btn lots-btn--ghost"
                  disabled={!hasStarted}
                >
                  {cmd}
                </button>
              ))}
            </div>
            <div className="portal-note" style={{ marginTop: '0.5rem' }}>
              Use a dedicated MUD client for the full experience. This web client is a quick cockpit.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MudClient;
export { MudClient };
